import React, { useState, useEffect, useMemo } from 'react';
import { Form, Upload, Image, Button, App } from 'antd';
import { PictureOutlined } from '@ant-design/icons';
import { resolveMedia } from '../../utils/media';

const { useApp } = App;
const { Dragger } = Upload;

// Extensiones permitidas para imágenes
const ALLOWED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
// Extensiones permitidas para videos
const ALLOWED_VIDEO_EXTENSIONS = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'm4v', 'mpg'];

// MIME types permitidos para imágenes
const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/svg+xml',
];

// MIME types permitidos para videos
const ALLOWED_VIDEO_TYPES = [
  'video/mp4',
  'video/webm',
  'video/ogg',
  'video/quicktime', // mov
  'video/x-msvideo', // avi
  'video/x-m4v', // m4v
  'video/mpeg', // mpg
];

const MediaUpload = ({
  allowVideo = true,
  maxSizeImage = 15,
  maxSizeVideo = 15,
  required = false,
  label = 'Imagen / Video',
  originalMediaPath = null,
  originalPreviewPath = null,
  isEdit = false,
  onChange,
  onRemove,
  value,
  form,
  name = 'media',
}) => {
  const { message } = useApp();
  
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(undefined);
  const [mediaType, setMediaType] = useState(null);
  const [mediaRemoved, setMediaRemoved] = useState(false);

  useEffect(() => {
    const pathToUse = originalMediaPath || originalPreviewPath;
    
    if (pathToUse) {
      setMediaPreview(resolveMedia(pathToUse));
      
      if (originalMediaPath) {
        const fileExtension = originalMediaPath.toLowerCase().split('.').pop();
        const isVideo = ALLOWED_VIDEO_EXTENSIONS.includes(fileExtension);
        setMediaType(isVideo ? 'video' : 'image');
      } else if (originalPreviewPath) {
        setMediaType('image');
      }
    }
  }, [originalMediaPath, originalPreviewPath]);

  useEffect(() => {
    if (onChange) {
      onChange({
        file: mediaFile,
        preview: mediaPreview,
        type: mediaType,
        removed: mediaRemoved,
      });
    }
  }, [mediaFile, mediaPreview, mediaType, mediaRemoved, onChange]);

  const uploadProps = useMemo(
    () => ({
      name: 'file',
      multiple: false,
      maxCount: 1,
      beforeUpload: (file) => {
        const fileName = file.name.toLowerCase();
        const fileExtension = fileName.includes('.') ? fileName.split('.').pop() : null;
        
        const isValidImageType = ALLOWED_IMAGE_TYPES.includes(file.type);
        const isValidVideoType = ALLOWED_VIDEO_TYPES.includes(file.type);
        
        const isValidImageExtension = fileExtension && ALLOWED_IMAGE_EXTENSIONS.includes(fileExtension);
        const isValidVideoExtension = fileExtension && ALLOWED_VIDEO_EXTENSIONS.includes(fileExtension);
        
        const isImage = isValidImageType && isValidImageExtension;
        const isVideo = isValidVideoType && isValidVideoExtension;
        
        if (!isImage && (!isVideo || !allowVideo)) {
          if (allowVideo) {
            const allowedFormats = ALLOWED_IMAGE_EXTENSIONS.join(', ').toUpperCase() + 
              ' o ' + ALLOWED_VIDEO_EXTENSIONS.join(', ').toUpperCase();
            message.error(`Formato no permitido. Solo se permiten: ${allowedFormats}`);
          } else {
            const allowedFormats = ALLOWED_IMAGE_EXTENSIONS.join(', ').toUpperCase();
            message.error(`Formato no permitido. Solo se permiten imágenes: ${allowedFormats}`);
          }
          return Upload.LIST_IGNORE;
        }
        
        const maxSize = isVideo ? maxSizeVideo : maxSizeImage;
        const isWithinMaxSize = file.size / 1024 / 1024 <= maxSize;
        if (!isWithinMaxSize) {
          message.error(`El archivo no puede exceder ${maxSize}MB`);
          return Upload.LIST_IGNORE;
        }

        setMediaFile(file);
        setMediaRemoved(false);
        
        if (isImage) {
          setMediaPreview(URL.createObjectURL(file));
          setMediaType('image');
        } else if (isVideo) {
          setMediaPreview(URL.createObjectURL(file));
          setMediaType('video');
        }
        
        return false;
      },
      onRemove: () => {
        setMediaFile(null);
        setMediaPreview(undefined);
        setMediaType(null);
        setMediaRemoved(true);
        if (onRemove) {
          onRemove();
        }
      },
      showUploadList: false,
    }),
    [allowVideo, maxSizeImage, maxSizeVideo, message, onRemove]
  );

  const handleRemove = () => {
    setMediaFile(null);
    setMediaPreview(undefined);
    setMediaType(null);
    setMediaRemoved(true);
    if (onRemove) {
      onRemove();
    }
  };

  const getFileName = () => {
    if (mediaFile) {
      return mediaFile.name;
    }
    if (isEdit && mediaType === 'video' && originalMediaPath) {
      return originalMediaPath.split('/').pop() || 'Video';
    }
    return null;
  };

  const fileName = getFileName();

  return (
    <Form.Item
      label={label}
      required={required}
      rules={required ? [
        {
          validator: () => {
            const hasOriginalMedia = originalMediaPath || originalPreviewPath;
            if (!mediaFile && !hasOriginalMedia && !isEdit) {
              return Promise.reject(new Error(allowVideo 
                ? 'La imagen o video es requerido' 
                : 'La imagen es requerida'));
            }
            if (mediaRemoved && !mediaFile) {
              return Promise.reject(new Error(allowVideo 
                ? 'La imagen o video es requerido' 
                : 'La imagen es requerida'));
            }
            return Promise.resolve();
          },
        },
      ] : []}
    >
      {mediaPreview ? (
        <div style={{ marginBottom: 16 }}>
          {mediaType === 'video' ? (
            <div
              style={{
                width: 200,
                height: 200,
                border: '1px solid #d9d9d9',
                borderRadius: 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
                position: 'relative',
              }}
            >
              <video
                src={mediaPreview}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 8,
                }}
                controls
              />
              {fileName && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 8,
                    left: 8,
                    right: 8,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: 4,
                    fontSize: 12,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fileName}
                </div>
              )}
            </div>
          ) : (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Image
                src={mediaPreview}
                alt="Preview"
                width={200}
                height={200}
                style={{ objectFit: 'cover', borderRadius: 8 }}
                preview
              />
              {mediaFile && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 8,
                    left: 8,
                    right: 8,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: 4,
                    fontSize: 12,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {mediaFile.name}
                </div>
              )}
            </div>
          )}
          <Button
            type="link"
            danger
            onClick={handleRemove}
            style={{ display: 'block', marginTop: 8 }}
          >
            Eliminar {mediaType === 'video' ? 'video' : 'imagen'}
          </Button>
        </div>
      ) : null}
      <Dragger {...uploadProps}>
        <p className="ant-upload-drag-icon">
          <PictureOutlined style={{ fontSize: 48, color: '#1890ff' }} />
        </p>
        <p className="ant-upload-text">
          {allowVideo ? 'Subir imagen o video' : 'Subir imagen'}
        </p>
        <p className="ant-upload-hint">
          {allowVideo 
            ? 'Haz clic o arrastra una imagen o video aquí para subirlo'
            : 'Haz clic o arrastra una imagen aquí para subirla'}
        </p>
      </Dragger>
    </Form.Item>
  );
};

export default MediaUpload;

